import { ReactNode, useCallback } from "react";
import { approveAccrualReview } from "@api/accrual-review.api";
import { getApiErrorMessage } from "@api/apiError";
import { SchedulePeriodLockedError } from "@api/scheduleErrors";
import { SCHEDULE_SOURCE_MOVE_CHANGED_MESSAGE } from "@common/helpers/domains/accounting/scheduleSourceMoves";
import { SUPPRESS_ERROR_TOAST_HEADERS } from "@api/scheduleTemplate.api";
import { ApproveAccrualReviewBody } from "@common/schemas/domains/accrualReview/accrual-review.schema";
import {
    MatchScheduleConflict,
    ScheduleMatchResolution,
    ScheduleSourceMoveConflictErrorBody,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { ScheduleMatchResolutionKind } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    getScheduleLifecyclePeriodLockedError,
    getScheduleLinkedSourceConflict,
    getScheduleSourceMoveConflict,
} from "@helpers/lock";
import { useInlineScheduleLockedPeriods } from "@components/accounting/schedules/useInlineScheduleLockedPeriods";
import { useScheduleConflictWizard } from "./useScheduleConflictWizard";
import { useScheduleSourceMoveConfirm } from "./useScheduleSourceMoveConfirm";

export const NOTHING_LEFT_TO_MATCH_MESSAGE =
    "Not matched; each side keeps its schedule";

interface MoveResult {
    kind: "move";
    moves: ScheduleSourceMoveConflictErrorBody["moves"];
}

type SentRequest<TResult> = { kind: "sent"; result: TResult } | MoveResult;

function toMoveResult(
    requestError: Parameters<typeof getScheduleSourceMoveConflict>[0],
): MoveResult | null {
    const moves = getScheduleSourceMoveConflict(requestError)?.moves;
    return moves ? { kind: "move", moves } : null;
}

/** `approved` saved; `cancelled` the user backed out; `skipped` every link was unmatched. */
export type ApproveWithScheduleResolutionsOutcome =
    | "approved"
    | "cancelled"
    | "skipped";

interface ApproveRequestOptions {
    scheduleResolutions: ScheduleMatchResolution[];
    extraHeaders: Record<string, string>;
}

export function approveAccrualReviewRequest(options: {
    workspaceId: string;
    body: ApproveAccrualReviewBody;
}) {
    return ({ scheduleResolutions, extraHeaders }: ApproveRequestOptions) =>
        approveAccrualReview({
            workspaceId: options.workspaceId,
            body: { ...options.body, scheduleResolutions },
            extraHeaders,
        });
}

/**
 * A match save that answers the server's "both ends scheduled" refusal with
 * the conflict wizard and resends with the user's resolutions. The first
 * request goes out without resolutions; the 409 body carries every conflict
 * the wizard needs, so no surface has to derive them from its rows.
 *
 * `hasWorkLeft` tells whether anything would still be matched once the
 * `unmatch` answers are applied; when nothing is, no request is sent.
 */
export function useApproveWithScheduleResolutions(): {
    approveWithScheduleResolutions: <TResult>(options: {
        request: (requestOptions: ApproveRequestOptions) => Promise<TResult>;
        hasWorkLeft?: (resolutions: ScheduleMatchResolution[]) => boolean;
        onApproved?: (result: TResult) => Promise<void> | void;
        /** Drop the unmatched rows from the open drawer. The save is not sent. */
        onUnmatched?: (resolutions: ScheduleMatchResolution[]) => void;
    }) => Promise<ApproveWithScheduleResolutionsOutcome>;
    scheduleResolutionModals: ReactNode;
} {
    const { error, toast } = useToaster();
    const { runScheduleConflictWizard, scheduleConflictWizard } =
        useScheduleConflictWizard();
    const { runScheduleSourceMoveConfirm, scheduleSourceMoveConfirm } =
        useScheduleSourceMoveConfirm();
    const { showLockedPeriods, lockedPeriodsModal } =
        useInlineScheduleLockedPeriods();

    const approveWithScheduleResolutions = useCallback(
        async <TResult,>(options: {
            request: (
                requestOptions: ApproveRequestOptions,
            ) => Promise<TResult>;
            hasWorkLeft?: (resolutions: ScheduleMatchResolution[]) => boolean;
            onApproved?: (result: TResult) => Promise<void> | void;
            onUnmatched?: (resolutions: ScheduleMatchResolution[]) => void;
        }): Promise<ApproveWithScheduleResolutionsOutcome> => {
            const send = (scheduleResolutions: ScheduleMatchResolution[]) =>
                options.request({
                    scheduleResolutions,
                    extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                });
            const reportFailure = (
                requestError: Parameters<typeof getApiErrorMessage>[0],
            ) => {
                const locked =
                    getScheduleLifecyclePeriodLockedError(requestError);
                if (locked) {
                    showLockedPeriods(new SchedulePeriodLockedError(locked));
                    return;
                }
                error(getApiErrorMessage(requestError, "Failed to save match"));
            };

            const sendFirst = async (): Promise<
                | SentRequest<TResult>
                | { kind: "conflict"; conflicts: MatchScheduleConflict[] }
            > => {
                try {
                    return { kind: "sent", result: await send([]) };
                } catch (requestError) {
                    const moveResult = toMoveResult(requestError);
                    if (moveResult) {
                        return moveResult;
                    }
                    const conflicts =
                        getScheduleLinkedSourceConflict(
                            requestError,
                        )?.conflicts;
                    if (!conflicts) {
                        reportFailure(requestError);
                        throw requestError;
                    }
                    return { kind: "conflict", conflicts };
                }
            };
            const sendMove = async (
                priorResolutions: ScheduleMatchResolution[],
                moves: ScheduleSourceMoveConflictErrorBody["moves"],
            ): Promise<ApproveWithScheduleResolutionsOutcome> => {
                const confirmed = await runScheduleSourceMoveConfirm(moves);
                if (!confirmed) {
                    return "cancelled";
                }
                const sent = await sendResolved([
                    ...priorResolutions,
                    ...moves.map((move) => ({
                        invoiceId: move.documentId,
                        resolution: ScheduleMatchResolutionKind.moveToDocument,
                        transactionIds: [move.transactionId],
                    })),
                ]);
                if (sent.kind === "move") {
                    error(SCHEDULE_SOURCE_MOVE_CHANGED_MESSAGE);
                    return "cancelled";
                }
                await options.onApproved?.(sent.result);
                return "approved";
            };
            const sendResolved = async (
                resolutions: ScheduleMatchResolution[],
            ): Promise<SentRequest<TResult>> => {
                try {
                    return { kind: "sent", result: await send(resolutions) };
                } catch (requestError) {
                    const moveResult = toMoveResult(requestError);
                    if (moveResult) {
                        return moveResult;
                    }
                    if (getScheduleLinkedSourceConflict(requestError)) {
                        error(
                            "Recognition schedules changed since you opened this; review and try again",
                        );
                    } else {
                        reportFailure(requestError);
                    }
                    throw requestError;
                }
            };

            const first = await sendFirst();
            if (first.kind === "sent") {
                await options.onApproved?.(first.result);
                return "approved";
            }
            if (first.kind === "move") {
                return await sendMove([], first.moves);
            }

            const outcome = await runScheduleConflictWizard(first.conflicts);
            if (outcome.kind !== "resolved") {
                return "cancelled";
            }
            if (
                options.hasWorkLeft &&
                !options.hasWorkLeft(outcome.resolutions)
            ) {
                options.onUnmatched?.(outcome.resolutions);
                toast(NOTHING_LEFT_TO_MATCH_MESSAGE);
                return "skipped";
            }
            const sent = await sendResolved(outcome.resolutions);
            if (sent.kind === "move") {
                return await sendMove(outcome.resolutions, sent.moves);
            }
            await options.onApproved?.(sent.result);
            return "approved";
        },
        [
            error,
            runScheduleConflictWizard,
            runScheduleSourceMoveConfirm,
            showLockedPeriods,
            toast,
        ],
    );

    return {
        approveWithScheduleResolutions,
        scheduleResolutionModals: (
            <>
                {scheduleConflictWizard}
                {scheduleSourceMoveConfirm}
                {lockedPeriodsModal}
            </>
        ),
    };
}
